import type {
  BaseRecord,
  CrudFilter,
  CrudFilters,
  CrudSorting,
  DataProvider,
  HttpError,
  Pagination,
} from "@refinedev/core";

import { getAccessToken } from "./auth/tokenStore";

// jmix-exit E3a.1: Refine DataProvider bound to the future Epic-6 API contract:
//   GET  /api/<resource>        -> array OR a Spring-Data page envelope { content, totalElements }
//   GET  /api/<resource>/{id}   -> object
//   POST /api/<resource>        -> 201 created body
// Ported from backoffice's frontend. jmix review 3a-T3: getList now passes pagination/sort/filter
// through to the API query params (Spring-Data convention: page/size 0-based, sort=field,order,
// field[_op]=value) and derives `total` from the response — an `X-Total-Count` header, a
// { content, totalElements } page envelope, or (back-compat, today's bare-array endpoint) the array
// length. So the provider is correct both now and once the live admin-CRUD endpoints land in E9.
// The bearer is attached from the in-memory token store when present (Epic 3b mints it). HTTP
// failures surface as Refine HttpError so screens get a typed { statusCode }. Dev: the Vite proxy
// forwards /api to the platform API; single-artifact: same-origin.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function authHeaders(body?: unknown): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function asHttpError(response: Response): HttpError {
  return {
    message: response.statusText || `HTTP ${response.status}`,
    statusCode: response.status,
  };
}

async function httpFetch(path: string, method: Method = "GET", body?: unknown): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(body),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw asHttpError(response);
  }
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

/** Only logical filters (those with a concrete `field`) map to a query param; conditional groups are skipped. */
function isLogicalFilter(filter: CrudFilter): filter is Extract<CrudFilter, { field: string }> {
  return "field" in filter && filter.field !== undefined;
}

/** Build the list query string from Refine's pagination/sorters/filters (Spring-Data conventions). */
export function buildListQuery(
  pagination?: Pagination,
  sorters?: CrudSorting,
  filters?: CrudFilters,
): string {
  const params = new URLSearchParams();

  if (pagination && pagination.mode !== "off") {
    const current = pagination.current;
    const pageSize = pagination.pageSize;
    if (typeof current === "number" && typeof pageSize === "number") {
      params.set("page", String(Math.max(0, current - 1))); // Refine is 1-based, Spring is 0-based
      params.set("size", String(pageSize));
    }
  }

  for (const sorter of sorters ?? []) {
    params.append("sort", `${sorter.field},${sorter.order}`);
  }

  for (const filter of filters ?? []) {
    if (!isLogicalFilter(filter) || filter.value === undefined || filter.value === null) {
      continue;
    }
    const key = filter.operator && filter.operator !== "eq"
      ? `${filter.field}_${filter.operator}`
      : filter.field;
    params.set(key, String(filter.value));
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

/** Derive { data, total } from a list response: page envelope, X-Total-Count header, or bare array. */
function parseListBody(body: unknown, totalHeader: string | null): { data: never[]; total: number } {
  if (Array.isArray(body)) {
    const total = totalHeader != null ? Number(totalHeader) : body.length;
    return { data: body as never[], total: Number.isFinite(total) ? total : body.length };
  }
  if (body && typeof body === "object" && Array.isArray((body as { content?: unknown }).content)) {
    const envelope = body as { content: unknown[]; totalElements?: number };
    const total = envelope.totalElements ?? envelope.content.length;
    return { data: envelope.content as never[], total };
  }
  return { data: [] as never[], total: 0 };
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, sorters, filters }) => {
    const query = buildListQuery(pagination, sorters, filters);
    const response = await fetch(`${API_URL}/${resource}${query}`, { headers: authHeaders() });
    if (!response.ok) {
      throw asHttpError(response);
    }
    const text = await response.text();
    const body = text ? JSON.parse(text) : [];
    return parseListBody(body, response.headers.get("X-Total-Count"));
  },

  getOne: async ({ resource, id }) => {
    const body = await httpFetch(`/${resource}/${id}`);
    return { data: body as never };
  },

  getMany: async ({ resource, ids }) => {
    const all = await httpFetch(`/${resource}`);
    const rows = (Array.isArray(all) ? all : []) as BaseRecord[];
    const wanted = new Set(ids.map(String));
    return { data: rows.filter((r) => wanted.has(String(r.id))) as never[] };
  },

  create: async ({ resource, variables }) => {
    const body = await httpFetch(`/${resource}`, "POST", variables);
    return { data: body as never };
  },

  update: async ({ resource, id, variables }) => {
    const body = await httpFetch(`/${resource}/${id}`, "PUT", variables);
    return { data: (body ?? { id, ...(variables as object) }) as never };
  },

  deleteOne: async ({ resource, id }) => {
    const body = await httpFetch(`/${resource}/${id}`, "DELETE");
    return { data: (body ?? { id }) as never };
  },

  custom: async ({ url, method, payload }) => {
    const path = url.startsWith("/") ? url : `/${url}`;
    const verb = (method ?? "get").toUpperCase() as Method;
    const body = await httpFetch(path, verb, verb === "GET" ? undefined : payload);
    return { data: body as never };
  },
};
